import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';
import useSyncedState from './useSyncedState';
import ReferenceScale from './ReferenceScale';

// Mind map — a central topic with branches around it and leaves on each
// branch (two levels). The teacher can pre-seed branches/leaves in the config;
// during the lesson the student (and teacher) add their own nodes with "+".
// Synced state: { added: [{ id, parentId: 'center' | branchId, text }], rev }.
// Seeded nodes are read-only at runtime; added ones can be edited or removed.

export const MIND_MAP_DEFAULT_COLORS = ['#2A4D25', '#e11d48', '#2563eb', '#f59e0b', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];

const createInitialState = () => ({ added: [] });

const polar = (cx, cy, rx, ry, deg) => {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + rx * Math.cos(rad), y: cy + ry * Math.sin(rad) };
};

const NodeInput = ({ initial, onCommit, style }) => {
  const [value, setValue] = useState(initial || '');
  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onCommit(value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommit(value);
        if (e.key === 'Escape') onCommit(initial || '');
      }}
      onPointerDown={(e) => e.stopPropagation()}
      className="bg-transparent outline-none text-center min-w-0"
      style={{ ...style, width: `${Math.max(6, value.length + 2)}ch`, maxWidth: '100%' }}
    />
  );
};

const MindMapInner = ({
  element,
  isEditor = false,
  canInteract = true,
  syncedState = null,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const {
    centerText = '',
    centerImageUrl = null,
    branches = [],
    allowAdd = true,
    centerColor = '#2A4D25',
    centerTextColor = '#ffffff',
    branchColors = MIND_MAP_DEFAULT_COLORS,
    nodeBgColor = '#ffffff',
    textColor = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 16,
  } = element.content || {};

  const [state, update] = useSyncedState(syncedState, onStateChange, createInitialState);
  const [editingId, setEditingId] = useState(null);
  const interactive = !isEditor && canInteract;
  const canAdd = interactive && allowAdd;

  const added = Array.isArray(state.added) ? state.added : [];
  const allBranches = [
    ...branches.map((b) => ({ ...b, seeded: true })),
    ...added.filter((n) => n.parentId === 'center'),
  ].filter((b) => b.seeded || b.text || b.id === editingId);

  const leavesOf = (branch) => [
    ...(branch.seeded ? (branch.children || []).map((c) => ({ ...c, seeded: true })) : []),
    ...added.filter((n) => n.parentId === branch.id),
  ].filter((l) => l.seeded || l.text || l.id === editingId);

  const addNode = (parentId) => {
    const id = `mm_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    update((prev) => ({ ...prev, added: [...(prev.added || []), { id, parentId, text: '' }] }));
    setEditingId(id);
  };

  const commitNode = (id, text) => {
    setEditingId(null);
    const trimmed = text.trim();
    update((prev) => {
      const list = prev.added || [];
      const node = list.find((n) => n.id === id);
      if (!node) return prev;
      if (!trimmed) return { ...prev, added: list.filter((n) => n.id !== id && n.parentId !== id) };
      if (node.text === trimmed) return prev;
      return { ...prev, added: list.map((n) => (n.id === id ? { ...n, text: trimmed } : n)) };
    });
  };

  const removeNode = (id) => update((prev) => ({
    ...prev,
    added: (prev.added || []).filter((n) => n.id !== id && n.parentId !== id),
  }));

  const n = Math.max(1, allBranches.length);
  const arc = Math.min(110, (360 / n) * 0.8);
  const layout = allBranches.map((b, i) => {
    const angle = -90 + (i * 360) / n;
    const color = b.color || branchColors[i % branchColors.length] || MIND_MAP_DEFAULT_COLORS[0];
    const pos = polar(50, 50, 27, 29, angle);
    const leaves = leavesOf(b);
    const m = leaves.length;
    const leafLayout = leaves.map((l, j) => {
      const a = m > 1 ? angle - arc / 2 + (j * arc) / (m - 1) : angle;
      return { node: l, ...polar(50, 50, 43, 42, a) };
    });
    return { node: b, color, ...pos, leaves: leafLayout };
  });

  const fs = fontSize;

  const renderNode = ({ node, x, y, color, level }) => {
    const editing = editingId === node.id;
    const removable = interactive && !node.seeded;
    const isBranch = level === 1;
    return (
      <div
        key={node.id}
        className="absolute group"
        style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)', maxWidth: isBranch ? '24%' : '20%', zIndex: isBranch ? 3 : 2 }}
      >
        <div
          className="relative rounded-full text-center leading-tight break-words shadow-sm"
          onDoubleClick={() => { if (removable) setEditingId(node.id); }}
          style={{
            fontSize: isBranch ? fs : fs * 0.85,
            fontWeight: isBranch ? 600 : 400,
            padding: isBranch ? '6px 14px' : '4px 12px',
            backgroundColor: isBranch ? color : nodeBgColor,
            color: isBranch ? '#ffffff' : textColor,
            border: `2px solid ${color}`,
          }}
        >
          {editing ? (
            <NodeInput
              initial={node.text}
              onCommit={(v) => commitNode(node.id, v)}
              style={{ color: isBranch ? '#ffffff' : textColor, fontSize: 'inherit', fontWeight: 'inherit', fontFamily }}
            />
          ) : (node.text || '…')}
          {removable && !editing && (
            <button
              type="button"
              onClick={() => removeNode(node.id)}
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-300 items-center justify-center hidden group-hover:flex"
            >
              <X size={11} />
            </button>
          )}
          {isBranch && canAdd && !editing && (
            <button
              type="button"
              onClick={() => addNode(node.id)}
              title={t('games.mindMap.addLeaf', { defaultValue: 'Додати ідею' })}
              className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center"
              style={{ color, border: `1.5px solid ${color}` }}
            >
              <Plus size={12} strokeWidth={3} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative w-full h-full overflow-hidden" style={{ fontFamily, color: textColor }}>
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
        {layout.map((b) => (
          <g key={b.node.id}>
            <line x1="50" y1="50" x2={b.x} y2={b.y} stroke={b.color} strokeWidth="4" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            {b.leaves.map((l) => (
              <line key={l.node.id} x1={b.x} y1={b.y} x2={l.x} y2={l.y} stroke={b.color} strokeWidth="2.5" strokeLinecap="round" opacity="0.7" vectorEffect="non-scaling-stroke" />
            ))}
          </g>
        ))}
      </svg>

      {layout.map((b) => (
        <React.Fragment key={b.node.id}>
          {renderNode({ node: b.node, x: b.x, y: b.y, color: b.color, level: 1 })}
          {b.leaves.map((l) => renderNode({ node: l.node, x: l.x, y: l.y, color: b.color, level: 2 }))}
        </React.Fragment>
      ))}

      {/* Central topic */}
      <div className="absolute" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', maxWidth: '30%', zIndex: 4 }}>
        <div
          className="relative rounded-3xl shadow-md flex flex-col items-center text-center leading-tight break-words"
          style={{ backgroundColor: centerColor, color: centerTextColor, padding: '10px 20px', fontSize: fs * 1.35, fontWeight: 700, gap: 6 }}
        >
          {centerImageUrl && (
            <img src={resolveMediaUrl(centerImageUrl) || ''} alt="" draggable={false} className="max-h-20 max-w-full object-contain rounded-lg" />
          )}
          {centerText || (isEditor ? 'Topic' : '')}
          {canAdd && (
            <button
              type="button"
              onClick={() => addNode('center')}
              title={t('games.mindMap.addBranch', { defaultValue: 'Додати гілку' })}
              className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center"
              style={{ color: centerColor, border: `2px solid ${centerColor}` }}
            >
              <Plus size={14} strokeWidth={3} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const MindMapElement = ({ slideScale = 1, ...props }) => (
  <ReferenceScale scale={slideScale}>
    <MindMapInner {...props} />
  </ReferenceScale>
);

export default MindMapElement;
